import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit2, Trash2, Plus, Search } from 'lucide-react';
import styles from './CSS/BlogContent.module.css';
import { useBlogs, useDeleteBlog } from '../../../hooks/admin/useBlogs';

// Utility function to preserve paragraphs while stripping other HTML
const stripHtmlPreserveParagraphs = (html, maxLength = 150) => {
  if (!html) return '';
  
  // First replace break tags with newlines
  let content = html.replace(/<br\s*\/?>/gi, '\n');
  
  // Replace paragraph and header closing tags with double newlines
  content = content.replace(/<\/(p|h[1-6])>/gi, '\n\n');
  
  // Remove all remaining HTML tags
  content = content.replace(/<[^>]+>/g, '');
  
  // Decode HTML entities
  const temp = document.createElement('div');
  temp.innerHTML = content;
  let text = temp.textContent || temp.innerText;
  
  // Remove extra whitespace and normalize newlines
  text = text.replace(/\n\s*\n/g, '\n\n').trim();
  
  // Truncate if needed and add ellipsis
  if (maxLength && text.length > maxLength) {
    // Try to find the last complete paragraph within the limit
    const lastParagraphBreak = text.lastIndexOf('\n\n', maxLength - 3);
    if (lastParagraphBreak > maxLength / 2) {
      // If we can find a paragraph break in the latter half, break there
      text = text.substring(0, lastParagraphBreak).trim() + '...';
    } else {
      // Otherwise just truncate at the maximum length
      text = text.substring(0, maxLength).trim() + '...';
    }
  }
  
  return text;
};

const BlogContent = () => {
  const navigate = useNavigate();
  const { blogs, isLoading, refetchBlogs } = useBlogs();
  const deleteBlogMutation = useDeleteBlog();
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState([]);

  useEffect(() => {
    refetchBlogs();
  }, [refetchBlogs]);

  useEffect(() => {
    const blogsArray = Array.isArray(blogs) ? blogs : [];
    if (searchTerm.trim() === '') {
      setFilteredBlogs(blogsArray);
    } else {
      const filtered = blogsArray.filter(blog => {
        const title = stripHtmlPreserveParagraphs(blog.title || '').toLowerCase();
        const content = stripHtmlPreserveParagraphs(blog.content || '').toLowerCase();
        const author = stripHtmlPreserveParagraphs(blog.author_name || '').toLowerCase();
        const query = searchTerm.toLowerCase();
        
        return title.includes(query) || 
               content.includes(query) || 
               author.includes(query);
      });
      setFilteredBlogs(filtered);
    }
  }, [blogs, searchTerm]);

  const handleDelete = async (id) => {
    try {
      await deleteBlogMutation.mutateAsync(id);
      setBlogToDelete(null);
      refetchBlogs();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        Loading...
      </div>
    );
  }

  // Function to render text with preserved paragraphs
  const renderTextWithParagraphs = (text) => {
    return text.split('\n\n').map((paragraph, index) => (
      <p key={index} className={styles.paragraph}>
        {paragraph}
      </p>
    ));
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Blog Management</h1>
          <div className={styles.searchContainer}>
            <Search size={18} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search blogs..." 
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => navigate('/admin/add-blog')}
            className={styles.addButton}
          >
            <Plus size={20} />
            Add Blog
          </button>
        </div>

        {filteredBlogs.length > 0 ? (
          <div className={styles.grid}>
            {filteredBlogs.map((blog) => (
              <div key={blog.id} className={styles.card}>
                <div className={styles.imageContainer}>
                  <img
                    src={blog.featured_image || "/images/home/image1.png"}
                    alt={blog.title}
                    className={styles.image}
                  />
                  <div className={styles.status}>
                    {blog.status}
                  </div>
                </div>

                <div className={styles.content}>
                  <h2 className={styles.blogTitle}>
                    {stripHtmlPreserveParagraphs(blog.title, 100)}
                  </h2>
                  <div className={styles.description}>
                    {renderTextWithParagraphs(stripHtmlPreserveParagraphs(blog.meta_description || blog.content))}
                  </div>

                  <div className={styles.footer}>
                    <div className={styles.author}>
                      By {stripHtmlPreserveParagraphs(blog.author_name)}
                    </div>
                    <div className={styles.actions}>
                      <button
                        onClick={() => navigate(`/admin/edit-blog/${blog.id}`)}
                        className={styles.actionButton}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => setBlogToDelete(blog)}
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.error}>
            {searchTerm ? `No blogs found matching "${searchTerm}"` : "No blogs found"}
          </div>
        )}
      </div>

      {blogToDelete && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>
              Delete Blog
            </h3>
            <p className={styles.modalText}>
              Are you sure you want to delete "{stripHtmlPreserveParagraphs(blogToDelete.title)}"? This action cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button
                onClick={() => setBlogToDelete(null)}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(blogToDelete.id)}
                className={styles.confirmButton}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogContent;